import React, { useEffect, useState } from "react";
import { Box, CircularProgress, Dialog } from "@mui/material";
import { useTranslation } from "react-i18next";

import {
  Card,
  CommentArea,
  ConfirmButton,
  CancelButton,
  DialogActionsRow,
  DialogTitle,
  RatingButton,
  RatingLabel,
  RatingRow,
} from "@components/ChatAgent/components/ChatAgentFeedbackDialog/style";

export type FeedbackVote = "up" | "down" | "neutral";

const ratingToVote = (rating: number): FeedbackVote => {
  if (rating >= 4) return "up";
  if (rating <= 2) return "down";
  return "neutral";
};

export interface FeedbackPayload {
  feedback: FeedbackVote;
  rating: number;
  comment?: string;
}

interface ChatAgentFeedbackDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: FeedbackPayload) => Promise<void>;
  agentName?: string;
}

const RATING_VALUES = [1, 2, 3, 4, 5];

const ChatAgentFeedbackDialog = ({
  open,
  onClose,
  onSubmit,
  agentName,
}: ChatAgentFeedbackDialogProps) => {
  const { t } = useTranslation("agent_chat");
  const [rating, setRating] = useState<number | null>(null);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setRating(null);
      setComment("");
      setSubmitting(false);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (rating === null) return;
    setSubmitting(true);
    try {
      await onSubmit({
        feedback: ratingToVote(rating),
        rating,
        comment: comment.trim() || undefined,
      });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: "transparent",
          boxShadow: "none",
          overflow: "visible",
          maxWidth: "600px",
        },
      }}
    >
      <Box sx={{ padding: "16px" }}>
        <Card>
          <DialogTitle>
            {t("feedbackDialog.question", {
              agent: agentName ?? t("title"),
            })}
          </DialogTitle>

          <RatingRow>
            <RatingLabel>{t("feedbackDialog.lowLabel")}</RatingLabel>
            {RATING_VALUES.map((value) => {
              const selected = rating === value;
              return (
                <RatingButton
                  key={value}
                  selected={selected}
                  disabled={submitting}
                  onClick={() => setRating(value)}
                  aria-label={`rating ${value}`}
                >
                  {value}
                </RatingButton>
              );
            })}
            <RatingLabel>{t("feedbackDialog.highLabel")}</RatingLabel>
          </RatingRow>

          <CommentArea
            multiline
            minRows={4}
            maxRows={6}
            fullWidth
            inputProps={{ maxLength: 500 }}
            placeholder={t("feedbackDialog.commentPlaceholder") || ""}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            disabled={submitting}
          />

          <DialogActionsRow>
            <CancelButton onClick={onClose} disabled={submitting}>
              {t("feedbackDialog.cancel")}
            </CancelButton>
            <ConfirmButton
              onClick={handleSubmit}
              disabled={rating === null || submitting}
              endIcon={
                submitting ? (
                  <CircularProgress size={14} sx={{ color: "#000" }} />
                ) : undefined
              }
            >
              {t("feedbackDialog.submit")}
            </ConfirmButton>
          </DialogActionsRow>
        </Card>
      </Box>
    </Dialog>
  );
};

export default ChatAgentFeedbackDialog;
